import { IPortalUserRoleEnum } from 'Modules/Communication/PortalUsers/shared/models/PortalUsers'
import {
  FilterTypeEnum,
  IMeterAndObjectItemModel,
  IObjectAndMeterRecipientModel,
  IRecipientModel,
  IRecipientQueryModel,
  RecipientsSortByEnum,
} from 'Modules/Communication/shared/models/Recipients'
import { IBoardMember } from 'shared/models/Board'
import { ICommonListModel } from 'shared/models/Common'
import { IFilterModel, ISortOrderEnum } from 'shared/models/List'
import {
  BoardContactsSortFieldEnum,
  ContactsScopeEnum,
  ExtendedQueryTypeEnum,
  IBoardContact,
  IContactFilters,
  IOtherContact,
  IResidentContact,
  OtherContactsSortFieldEnum,
} from '../models/Contacts'
import { getPropertySectionNoFromIdFields } from 'Modules/HousingAssociation/OurCompany/shared/utils/companyTypeUtils'
import { CompanyTypeEnum } from 'shared/models/Client'

export const mapResidentContactToRecipient = (
  contacts: ICommonListModel<IResidentContact>,
  companyType: CompanyTypeEnum
): ICommonListModel<IRecipientModel> => {
  const result = contacts.result.map(
    (contact: IResidentContact): IRecipientModel => {
      const info = contact.unitInfo?.length
        ? contact.unitInfo.map((i) => i)
        : null

      let name = contact.name
      if (contact.role === IPortalUserRoleEnum.proxyOwner) {
        name = `${contact.name} (${contact.roleDescription})`
      }

      return {
        email: contact.email,
        name,
        nameId: contact.nameregisterId,
        phonenumber: contact.mobile || '',
        reservedAgainstElectronicCommunication: Boolean(
          contact.reservedAgainstElectronicCommunication
        ),
        hasSignedIn: Boolean(contact.hasSignedIn),
        address: info?.length
          ? info?.map(({ address }) => address).join(', ')
          : '',
        unitPropertyNo: info?.length
          ? info
              ?.map(({ unitIdentification }) =>
                getPropertySectionNoFromIdFields(
                  unitIdentification,
                  companyType
                )
              )
              .join(', ')
          : '',
        ssbNo: info?.length
          ? info
              ?.map(({ unitIdentification }) => unitIdentification.ssbNo)
              .join(', ')
          : '',
        unitIds: info?.map(({ unitId }) => unitId),
      }
    }
  )

  const data = {
    ...contacts,
    result,
  }

  return data
}

export const mapMeterOrObjectToRecipient = (
  meterOrObject: ICommonListModel<IObjectAndMeterRecipientModel>
): ICommonListModel<IRecipientModel> => {
  const result = meterOrObject.result.map(
    (contact: Partial<IObjectAndMeterRecipientModel>) => {
      const objectOrMeter =
        (contact.meters?.length && contact.meters) ||
        (contact.objects?.length && contact.objects) ||
        ([] as IMeterAndObjectItemModel[])

      return {
        address: contact?.address || '',
        email: contact?.email || '',
        name: contact?.name || '',
        nameId: contact?.nameId || '',
        phonenumber: contact?.mobile || '',
        reservedAgainstElectronicCommunication: Boolean(
          contact?.reservedAgainstElectronicCommunication
        ),
        unitPropertyNo: '',
        objectOrMeter,
        hasSignedIn: Boolean(contact?.hasSignedIn),
      }
    }
  )

  return {
    ...meterOrObject,
    result,
  }
}

export const mapBoardMembersToRecipient = (
  boardMembers: IBoardMember[]
): ICommonListModel<IRecipientModel> => {
  const result = boardMembers.map((member: IBoardMember) => {
    return {
      address: '',
      email: member.person.email,
      name: `${member.person.firstname} ${member.person.surname}`,
      nameId: member.person.id,
      phonenumber: member.person.mobile,
      reservedAgainstElectronicCommunication: false,
      unitPropertyNo: '',
      memberFunction: member.memberFunction,
    }
  })

  return {
    currentPage: 1,
    morePages: false,
    result,
    resultCount: result.length,
    totalCount: result.length,
    totalPageCount: Math.floor(result.length / 10),
  }
}

export const mapRecipientQueryToContactFilters = (
  query: IRecipientQueryModel,
  extendedQueryType?: ExtendedQueryTypeEnum
): IContactFilters => {
  const result: IContactFilters = {
    extendedQueryType,
    sortcolumn: String(query.sortcolumn),
    sortorder: String(query.sortorder),
    addressId: query.addressId || undefined,
    buildingId: query.buildingId ? Number(query.buildingId) : undefined,
    skip: query.skip,
    take: query.take,
    searchText: query.searchText || '',
    buildingTypeId: query.buildingTypeId
      ? Number(query.buildingTypeId)
      : undefined,
    contactRole:
      query.filterType === FilterTypeEnum.Owners
        ? ContactsScopeEnum.Owners
        : query.filterType === FilterTypeEnum.Tenants
          ? ContactsScopeEnum.Tenants
          : ContactsScopeEnum.Cohabitant,
  }

  return result
}

export const mapRecipientQueryToBoardContactFilters = (
  query: IRecipientQueryModel
): IFilterModel => {
  const column = query.sortcolumn as RecipientsSortByEnum

  let columnConvertion: string
  switch (column) {
    case RecipientsSortByEnum.Name:
      columnConvertion = BoardContactsSortFieldEnum.Name
      break
    case RecipientsSortByEnum.Email:
      columnConvertion = BoardContactsSortFieldEnum.Email
      break
    case RecipientsSortByEnum.Phonenumber:
      columnConvertion = BoardContactsSortFieldEnum.Mobile
      break
    case RecipientsSortByEnum.CompanyName:
      columnConvertion = BoardContactsSortFieldEnum.CompanyName
      break
    case RecipientsSortByEnum.Position:
      columnConvertion = BoardContactsSortFieldEnum.Position
      break

    default:
      columnConvertion = BoardContactsSortFieldEnum.Name
  }

  return {
    sortcolumn: columnConvertion,
    searchText: query.searchText || '',
    skip: query.skip,
    take: query.take,
    sortorder: query.sortorder || ISortOrderEnum.asc,
  }
}

export const mapRecipientQueryToOtherContactFilters = (
  query: IRecipientQueryModel
): IFilterModel => {
  const column = query.sortcolumn as RecipientsSortByEnum

  let columnConvertion: string
  switch (column) {
    case RecipientsSortByEnum.Name:
      columnConvertion = OtherContactsSortFieldEnum.Name
      break
    case RecipientsSortByEnum.Email:
      columnConvertion = OtherContactsSortFieldEnum.Email
      break
    case RecipientsSortByEnum.Phonenumber:
      columnConvertion = OtherContactsSortFieldEnum.Mobile
      break
    case RecipientsSortByEnum.CompanyName:
      columnConvertion = OtherContactsSortFieldEnum.CompanyName
      break
    case RecipientsSortByEnum.Position:
      columnConvertion = OtherContactsSortFieldEnum.Position
      break

    default:
      columnConvertion = OtherContactsSortFieldEnum.Name
  }

  return {
    sortcolumn: columnConvertion,
    searchText: query.searchText || '',
    skip: query.skip,
    take: query.take,
    sortorder: query.sortorder || ISortOrderEnum.asc,
  }
}

export const mapBoardContactsToRecipient = (
  boardContacts: ICommonListModel<IBoardContact>
): ICommonListModel<IRecipientModel> => {
  const result = boardContacts.result.map((contact: Partial<IBoardContact>) => {
    return {
      address: '',
      email: contact?.email || '',
      name: contact?.fullname || '',
      nameId: String(contact?.id) || '',
      phonenumber: contact?.mobile || contact.phone || '',
      reservedAgainstElectronicCommunication: false,
      unitPropertyNo: '',
      position: contact?.position || '',
      companyName: contact?.companyName || '',
    }
  })

  return {
    ...boardContacts,
    result,
  }
}

export const mapOtherContactsToRecipient = (
  boardContacts: ICommonListModel<IOtherContact>
): ICommonListModel<IRecipientModel> => {
  const result = boardContacts.result.map((contact: Partial<IOtherContact>) => {
    return {
      address: '',
      email: contact?.email || contact.workEmail || '',
      name: contact?.name || '',
      nameId: String(contact?.nameregisterId) || '',
      phonenumber: contact?.mobile || contact.workPhoneNumber || '',
      reservedAgainstElectronicCommunication: false,
      position: contact?.position || '',
      companyName: contact?.companyName || '',
      unitPropertyNo: '',
    }
  })

  return {
    ...boardContacts,
    result,
  }
}
